import { useState, useEffect } from "react";
import {
  uploadTripDocument,
  fetchTripDocuments,
  getDocumentUrl,
  deleteTripDocument,
} from "../lib/supabaseDocuments";
import type { ProgramItem } from "../pages/TripDetail";
import { format } from "date-fns";
import { useTripRealtimeSync } from "../lib/useTripRealtimeSync";
import FloatingActionButton from "../components/FloatingActionButton";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";
import { useLocation } from "react-router-dom";

interface TripDocumentsProps {
  tripId: string;
  userId: string;
  programItems: ProgramItem[];
  tripCreatorId: string;
  isOrganizer: boolean;
}

interface DocumentEntry {
  id: string;
  name: string | null;
  file_url: string;
  created_at: string | null;
  program_item_id: string | null;
  trip_id: string | null;
  creator_id: string | null;
}

const MAX_FILE_SIZE_MB = 5;

function getFileTypeEmoji(filePath: string) {
  const ext = filePath.split(".").pop()?.toLowerCase();
  if (!ext) return "📎";
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return "🖼️";
  if (["pdf"].includes(ext)) return "📄";
  return "📎";
}

export default function TripDocuments({
  tripId,
  userId,
  programItems,
  tripCreatorId,
  isOrganizer,
}: TripDocumentsProps) {
  const [documents, setDocuments] = useState<DocumentEntry[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [programItemId, setProgramItemId] = useState<string | "">("");
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { showConfirm } = useConfirmDialog();
  const loadDocuments = async () => {
    const { data, error } = await fetchTripDocuments(tripId);
    if (!error && data) {
      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
      );
      setDocuments(sorted);

      const urlMap: Record<string, string> = {};
      for (const doc of sorted) {
        const url = await getDocumentUrl(doc.file_url);
        if (url) urlMap[doc.id] = url;
      }
      setSignedUrls(urlMap);
    }
  };
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showAddDocument = searchParams.get("add") === "document";

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    if (showAddDocument) {
      setModalOpen(true);
    }
  }, [showAddDocument]);

  useTripRealtimeSync<DocumentEntry>({
    tripId,
    table: "trip_documents",
    setItems: setDocuments,
    getId: (doc) => doc.id,
  });

  // Direkt nach dem useTripRealtimeSync-Hook
  useEffect(() => {
    const updateMissingUrls = async () => {
      const missing = documents.filter((doc) => !signedUrls[doc.id]);

      if (missing.length === 0) return;

      const newUrls: Record<string, string> = {};

      for (const doc of missing) {
        const url = await getDocumentUrl(doc.file_url);
        if (url) {
          newUrls[doc.id] = url;
        }
      }

      if (Object.keys(newUrls).length > 0) {
        setSignedUrls((prev) => ({ ...prev, ...newUrls }));
      }
    };

    updateMissingUrls();
  }, [documents, signedUrls]);

  const handleUpload = async () => {
    setErrorMessage(null);

    if (!file) {
      setErrorMessage("Bitte wähle eine Datei aus.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrorMessage(
        `Die Datei darf maximal ${MAX_FILE_SIZE_MB} MB groß sein.`
      );
      return;
    }

    setUploading(true);

    const result = await uploadTripDocument(
      file,
      tripId,
      title,
      userId,
      programItemId
    );

    if (result.error) {
      setErrorMessage("❌ Fehler beim Hochladen: " + result.error.message);
    } else {
      setTitle("");
      setFile(null);
      setProgramItemId("");
      setModalOpen(false);
      await loadDocuments();
    }

    setUploading(false);
  };

  const handleDelete = async (doc: DocumentEntry) => {
    const confirmed = await showConfirm({
      title: "Dokument löschen",
      message: `Möchtest du das Dokument "${
        doc.name || doc.file_url
      }" wirklich löschen?`,
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
      icon: "warning",
    });

    if (!confirmed) return;

    const { error } = await deleteTripDocument(doc.id, doc.file_url);
    if (error) {
      alert("❌ Fehler beim Löschen: " + error.message);
    } else {
      await loadDocuments();
    }
  };

  return (
    <div className="mt-4">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Dokumente
      </h3>

      {documents.length === 0 && (
        <div className="text-center py-10">
          <p className="text-slate-500 text-sm">📎 Noch keine Dokumente vorhanden</p>
          {isOrganizer && (
            <p className="text-slate-700 text-xs mt-1">
              Lade Dokumente hoch, um sie mit der Gruppe zu teilen.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {documents.map((doc) => {
          const fileName = doc.file_url.split("/").pop();
          const displayName = doc.name?.trim() || fileName;
          const programItemTitle = doc.program_item_id
            ? programItems.find((p) => p.id === doc.program_item_id)?.title
            : null;
          const ext = doc.file_url.split(".").pop()?.toLowerCase();
          const isImage =
            ext && ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
          const createdAt = format(
            new Date(doc.created_at ?? ""),
            "dd.MM.yyyy HH:mm"
          );
          const fileUrl = signedUrls[doc.id];

          return (
            <div
              key={doc.id}
              className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors flex flex-col gap-2"
            >
              {/* Image preview at top */}
              {fileUrl && isImage && (
                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={fileUrl}
                    alt={displayName}
                    className="w-full h-28 object-cover rounded-lg"
                  />
                </a>
              )}

              {/* File name + emoji */}
              <div className="flex items-center gap-2">
                <span className="text-base leading-none shrink-0">
                  {getFileTypeEmoji(doc.file_url)}
                </span>
                <span className="text-sm font-semibold text-slate-100 truncate">
                  {displayName}
                </span>
              </div>

              {/* Program item badge */}
              {programItemTitle && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#1e2639] text-slate-400 self-start">
                  {programItemTitle}
                </span>
              )}

              {/* Date */}
              <div className="text-[10px] text-slate-600">{createdAt}</div>

              {/* Footer actions */}
              {fileUrl && (
                <div className="flex items-center gap-3 mt-auto pt-2 border-t border-[#1e2639]">
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    Öffnen
                  </a>
                  <a
                    href={fileUrl}
                    download
                    className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    Herunterladen
                  </a>
                  {isOrganizer && (
                    <button
                      onClick={() => handleDelete(doc)}
                      className="text-[11px] text-slate-600 hover:text-red-400 transition-colors ml-auto"
                    >
                      Löschen
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload Modal */}
      {isOrganizer && modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] max-w-lg w-full p-6 relative shadow-2xl">
            <button
              className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition-colors text-lg leading-none"
              onClick={() => setModalOpen(false)}
            >
              ✕
            </button>

            <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5">
              Dokument hochladen
            </h3>

            <input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-400 mb-3 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-[#1e2639] file:text-slate-300 hover:file:bg-[#253350] file:cursor-pointer"
            />

            <input
              type="text"
              placeholder="Titel (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-[#1e2639] border border-[#2a3347] rounded-xl text-sm text-slate-200 placeholder:text-slate-600 mb-3 outline-none focus:border-sky-500/50 transition-colors"
            />

            <select
              value={programItemId}
              onChange={(e) => setProgramItemId(e.target.value)}
              className="w-full px-3 py-2 bg-[#1e2639] border border-[#2a3347] rounded-xl text-sm text-slate-300 mb-3 outline-none focus:border-sky-500/50 transition-colors"
            >
              <option value="">– Optional: Programmpunkt zuordnen –</option>
              {programItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}{item.start_time ? ` (${item.start_time.slice(0, 10)})` : ""}
                </option>
              ))}
            </select>

            {errorMessage && (
              <p className="text-xs text-red-400 mb-3">{errorMessage}</p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm text-slate-400 hover:text-slate-200 bg-[#1e2639] transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="px-4 py-2 rounded-xl text-sm text-white bg-sky-500 hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? "Lade hoch…" : "Hochladen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB */}
      {isOrganizer && (
        <FloatingActionButton
          visible={true}
          localStorageKey="seen_fab_documents"
          actions={[
            {
              label: "Dokument hochladen",
              onClick: () => {
                setModalOpen(true);
                setFile(null);
                setTitle("");
                setProgramItemId("");
              },
            },
          ]}
        />
      )}
    </div>
  );
}
